/* Bazinga Nerd Shop — controllo disponibilita' delle grafiche R2 nel carrello.
   Non espone credenziali, non scarica file originali e non modifica ordini.
   Necessita dell'endpoint di sola lettura POST /public/r2-check del Worker V11. */
(() => {
  'use strict';
  const form = document.querySelector('.cart-page form.cart-layout');
  const checkout = form?.querySelector('[data-checkout-button]');
  if (!form || !checkout) return;
  // Il carrello senza personalizzazioni non ha bisogno di interrogare R2.
  if (checkout.dataset.bazingaR2Ready === 'ok') return;
  const consent = form.querySelector('[data-checkout-confirm]');
  const status = form.querySelector('[data-bazinga-r2-cart-status]');
  const workerUrl = 'https://bazinga-drive-backup.paganogaspare89.workers.dev/public/r2-check';
  const routeRoot = window.Shopify?.routes?.root || '/';
  const cartUrl = `${routeRoot.replace(/\/?$/, '/') }cart.js`;
  const recognizedModes = new Set(['grafica caricata', 'grafica e testo', 'solo testo']);
  let cartReady = false;
  let lastCheckedAt = 0;
  let verifying = null;
  let submissionAllowed = false;

  const normalize = value => String(value ?? '').normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '').replace(/^_/, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const entriesFor = properties => Object.entries(properties && typeof properties === 'object' ? properties : {});
  const lookup = (properties, label) => {
    const match = entriesFor(properties).find(([name]) => normalize(name) === normalize(label));
    return match ? String(match[1] ?? '') : '';
  };
  const isR2KeyLabel = name => /^(grafica originale|testo png originale) (fronte|retro) r2$/.test(normalize(name));
  const activeSides = value => {
    const pos = normalize(value);
    if (pos === 'retro') return ['retro'];
    if (pos === 'fronte e retro') return ['fronte', 'retro'];
    return ['fronte'];
  };

  const itemDetails = item => {
    const props = item.properties || {};
    const mode = normalize(lookup(props, 'Tipo personalizzazione'));
    const custom = recognizedModes.has(mode);
    const keys = [];
    const missing = [];
    for (const [name, value] of entriesFor(props)) {
      if (isR2KeyLabel(name) && value) {
        const side = normalize(name).includes('retro') ? 'retro' : 'fronte';
        const kind = normalize(name).startsWith('testo') ? 'testo' : 'grafica';
        const expectedSize = Number(lookup(props, `Dimensione ${kind} ${side} - byte`));
        keys.push({ key: String(value), expectedSize: Number.isFinite(expectedSize) && expectedSize > 0 ? expectedSize : null, side, kind });
      }
    }
    if (custom) {
      for (const side of activeSides(lookup(props, 'Posizione stampa'))) {
        if (mode !== 'solo testo' && !keys.some(k => k.side === side && k.kind === 'grafica')) missing.push(`${side} (grafica)`);
        if (mode !== 'grafica caricata' && !keys.some(k => k.side === side && k.kind === 'testo')) missing.push(`${side} (testo)`);
      }
    }
    return { needsCheck: custom || keys.length > 0, keys, missing };
  };

  const setStatus = (message, kind = 'checking') => {
    if (!status) return;
    status.hidden = !message;
    status.textContent = message;
    status.dataset.state = kind;
  };
  const setReady = ready => {
    cartReady = ready;
    checkout.dataset.bazingaR2Ready = ready ? 'ok' : 'blocked';
    const disabled = !ready || !consent?.checked;
    checkout.disabled = disabled;
    checkout.setAttribute('aria-disabled', String(disabled));
  };
  const hideAlerts = () => {
    form.querySelectorAll('[data-bazinga-r2-item-alert]').forEach(el => { el.hidden = true; });
  };
  const showAlert = (line, title, message) => {
    const item = Array.from(form.querySelectorAll('[data-bazinga-cart-item]'))
      .find(el => el.dataset.bazingaLineKey === line.key);
    const alert = item?.querySelector('[data-bazinga-r2-item-alert]');
    if (!alert) return;
    alert.hidden = false;
    alert.querySelector('[data-bazinga-r2-item-title]').textContent = title;
    alert.querySelector('[data-bazinga-r2-item-message]').textContent = message;
  };

  const fetchJson = async (url, options = {}, timeoutMs = 12000) => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal, cache: 'no-store' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return await response.json();
    } finally { window.clearTimeout(timeout); }
  };
  const performVerification = async () => {
    setReady(false);
    hideAlerts();
    setStatus('Controllo la disponibilità delle grafiche originali…');
    try {
      const cart = await fetchJson(cartUrl, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!Array.isArray(cart.items)) throw new Error('Carrello non disponibile');
      const items = cart.items.map(line => ({ line, ...itemDetails(line) })).filter(x => x.needsCheck);
      if (!items.length) {
        setReady(true);
        setStatus('', 'ok');
        lastCheckedAt = Date.now();
        return true;
      }
      let invalid = false;
      for (const item of items) {
        if (item.missing.length) {
          invalid = true;
          showAlert(item.line, 'Personalizzazione incompleta', 'I dati della grafica non sono completi. Rimuovi questo articolo e caricala nuovamente.');
        }
      }
      const allKeys = [...new Set(items.flatMap(item => item.keys.map(entry => entry.key)))];
      const results = Object.create(null);
      // L'endpoint accetta massimo 8 chiavi per richiesta.
      for (let i = 0; i < allKeys.length; i += 8) {
        const keys = allKeys.slice(i, i + 8);
        const answer = await fetchJson(workerUrl, {
          method: 'POST',
          mode: 'cors',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ keys })
        });
        if (!answer || !answer.results || typeof answer.results !== 'object') throw new Error('Risposta R2 non valida');
        for (const key of keys) {
          if (!Object.hasOwn(answer.results, key) || typeof answer.results[key]?.exists !== 'boolean') {
            throw new Error('Verifica R2 incompleta');
          }
          results[key] = answer.results[key];
        }
      }
      for (const item of items) {
        if (item.missing.length) continue;
        const unavailable = item.keys.some(entry => {
          const check = results[entry.key];
          return !check || !check.exists || (entry.expectedSize && check.size !== entry.expectedSize);
        });
        if (unavailable) {
          invalid = true;
          showAlert(item.line, 'Grafica non più disponibile', 'La grafica originale è stata eliminata o non è più disponibile. Rimuovi questo articolo dal carrello, torna alla personalizzazione e carica nuovamente il file.');
        }
      }
      setReady(!invalid);
      lastCheckedAt = invalid ? 0 : Date.now();
      setStatus(invalid ? 'Alcune grafiche non sono disponibili. Ricaricale prima di procedere al pagamento.' : 'Grafiche originali verificate: puoi proseguire con il checkout.', invalid ? 'blocked' : 'ok');
      return !invalid;
    } catch (error) {
      // Fallimento sicuro: nessun pagamento di articoli personalizzati senza verifica.
      setReady(false);
      setStatus('Non riesco a verificare le grafiche in questo momento. Riprova tra poco: il pagamento è temporaneamente sospeso.', 'blocked');
      console.warn('Bazinga R2 cart check unavailable:', error?.message || error);
      return false;
    }
  };
  const verify = () => {
    if (!verifying) verifying = performVerification().finally(() => { verifying = null; });
    return verifying;
  };

  // Ricaricamento guidato: solo quando il cliente clicca esplicitamente,
  // togliamo la vecchia riga non stampabile e apriamo la pagina prodotto.
  form.querySelectorAll('[data-bazinga-r2-reupload]').forEach(link => {
    link.addEventListener('click', async event => {
      event.preventDefault();
      if (link.dataset.busy === 'true') return;
      const lineKey = link.closest('[data-bazinga-cart-item]')?.dataset.bazingaLineKey;
      if (!lineKey) { setStatus('Rimuovi il vecchio articolo dal carrello, poi ricarica la grafica.', 'blocked'); return; }
      const destination = link.href;
      link.dataset.busy = 'true';
      link.setAttribute('aria-disabled', 'true');
      const oldLabel = link.textContent;
      link.textContent = 'Preparo il nuovo caricamento…';
      try {
        const response = await fetch(`${routeRoot.replace(/\/?$/, '/')}cart/change.js`, {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: lineKey, quantity: 0 }), cache: 'no-store'
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        window.location.assign(destination);
      } catch (error) {
        link.dataset.busy = 'false';
        link.removeAttribute('aria-disabled');
        link.textContent = oldLabel;
        setStatus('Non riesco a rimuovere l’articolo scaduto. Usa «Rimuovi» e poi torna alla personalizzazione.', 'blocked');
        console.warn('Bazinga old cart item removal failed:', error?.message || error);
      }
    });
  });

  // Controllo iniziale, al ritorno dal browser e al momento dell'acquisto.
  checkout.dataset.bazingaR2Ready = checkout.dataset.bazingaR2Ready || 'checking';
  if (checkout.dataset.bazingaR2Ready !== 'ok') setReady(false);
  consent?.addEventListener('change', () => {
    checkout.disabled = !cartReady || !consent.checked;
    checkout.setAttribute('aria-disabled', String(checkout.disabled));
  });
  form.addEventListener('submit', async event => {
    const isCheckout = event.submitter === checkout || event.submitter?.name === 'checkout';
    if (!isCheckout) return; // "Aggiorna carrello" e rimozione restano disponibili.
    if (submissionAllowed) { submissionAllowed = false; return; }
    event.preventDefault();
    if (!consent?.checked) {
      setStatus('Conferma le condizioni prima di procedere al checkout.', 'blocked');
      return;
    }
    // Anche una verifica precedente viene ripetuta qui per intercettare
    // un'eventuale cancellazione R2 nel frattempo.
    const valid = await verify();
    if (!valid) { status?.scrollIntoView({ behavior: 'smooth', block: 'center' }); return; }
    submissionAllowed = true;
    form.requestSubmit(checkout);
  }, true);
  window.addEventListener('pageshow', () => { submissionAllowed = false; void verify(); });
  // pageshow gestisce il caricamento iniziale: su alcuni browser non viene
  // emesso dopo DOMContentLoaded, quindi eseguiamo comunque un primo controllo.
  void verify();
})();
